import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";

import {
  CardConfig,
  NestGroupConfig,
  NestGroups,
  RowData,
} from "../nesting-summary-cards";
import { Injectable } from "@angular/core";
import type { NestingSummaryCardsComponent } from "../nesting-summary-cards.component";
import type { NestingCardsWorkflowDependencies } from "./nesting-cards-workflow-dependencies";

type NestingCardsPopupWorkflowContext = Pick<
  NestingSummaryCardsComponent,
  | "selectedCardConfig"
  | "popupActiveNestGroup"
  | "nestGroupConfigs"
  | "tempCardConfigs"
  | "allTempChanges"
  | "tempSelectedCardConfig"
  | "newNestGroupMapping"
  | "recalculateCardStats"
  | "originalStateBeforePopup"
  | "nestGroupsList"
  | "originalTotalNests"
  | "total_nests"
  | "originalNestGroupConfigsSnapshot"
  | "showMoveSkuPopup"
  | "toggleNestGroupInPopup"
  | "closeDialog"
>;
@Injectable({ providedIn: "root" })
export class NestingCardsPopupWorkflow {
  toggleNestGroupInPopup(
    context: NestingCardsPopupWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
    group: NestGroups,
  ): void {
    if (!context.selectedCardConfig) return;
    dependencies.saveTempChanges();
    context.popupActiveNestGroup = group;
    const match = context.nestGroupConfigs.find(
      (cfg) => cfg.nestGroupValue === group.value,
    );
    if (!match) return;
    const selectedNestNumber = context.selectedCardConfig.header.nestNumber;
    dependencies.applySavedSelectedRows(selectedNestNumber);

    const allOriginalNestNumbers = dependencies.collectAllOriginalNestNumbers();
    context.tempCardConfigs = dependencies.buildUpdatedTempConfigs(
      match,
      selectedNestNumber,
      group.value,
      allOriginalNestNumbers,
    );

    dependencies.recalculateTempConfigsStats();
  }
  applySavedSelectedRows(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    selectedNestNumber: number,
  ): void {
    const savedSelectedRows = context.allTempChanges.get(selectedNestNumber);
    if (savedSelectedRows) {
      context.tempSelectedCardConfig = {
        ...context.selectedCardConfig!,
        rows: [...savedSelectedRows],
      };
    }
  }
  collectAllOriginalNestNumbers(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): Set<number> {
    const allOriginalNestNumbers = new Set<number>();
    for (const grp of context.nestGroupConfigs) {
      for (const card of grp.cardConfigs) {
        allOriginalNestNumbers.add(card.header.nestNumber);
      }
    }
    return allOriginalNestNumbers;
  }
  buildUpdatedTempConfigs(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    match: NestGroupConfig,
    selectedNestNumber: number,
    groupValue: string,
    allOriginalNestNumbers: Set<number>,
  ): CardConfig[] {
    const updatedTempConfigs: CardConfig[] = [];
    for (const card of match.cardConfigs) {
      if (card.header.nestNumber === selectedNestNumber) continue;
      const savedRows = context.allTempChanges.get(card.header.nestNumber);
      const cardRows = savedRows
        ? [...savedRows]
        : card.rows.map((r) => ({ ...r }));
      updatedTempConfigs.push({ ...card, rows: cardRows });
    }
    for (const [nestNumber, rows] of context.allTempChanges.entries()) {
      if (
        nestNumber === selectedNestNumber ||
        allOriginalNestNumbers.has(nestNumber)
      )
        continue;
      const nestGroupValue = context.newNestGroupMapping.get(nestNumber);
      if (nestGroupValue === groupValue) {
        updatedTempConfigs.push({
          header: { nestNumber: nestNumber, pplRange: "-", beerType: "-" },
          stats: [
            { label: "# Brand", value: "-" },
            { label: "# Segment", value: "-" },
            { label: "# Pack Type", value: "-" },
            { label: "# Pack Size", value: "-" },
          ],
          columns: ["SKU Name", "PPL", "Segment", "Brand"],
          rows: [...rows],
        });
      }
    }

    return updatedTempConfigs;
  }
  recalculateTempConfigsStats(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    if (context.tempSelectedCardConfig) {
      context.recalculateCardStats(context.tempSelectedCardConfig.rows);
    }
    for (const tempCard of context.tempCardConfigs) {
      context.recalculateCardStats(tempCard.rows);
    }
  }
  saveTempChanges(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    if (context.tempSelectedCardConfig) {
      context.allTempChanges.set(
        context.tempSelectedCardConfig.header.nestNumber,
        [...context.tempSelectedCardConfig.rows],
      );
    }
    for (const tempCard of context.tempCardConfigs) {
      context.allTempChanges.set(tempCard.header.nestNumber, [
        ...tempCard.rows,
      ]);
    }
  }
  moveSku(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    config: CardConfig,
  ): void {
    context.allTempChanges.clear();
    context.originalStateBeforePopup.clear();
    context.newNestGroupMapping.clear();

    const activeGroup = context.nestGroupsList.find((g) => g.isSelected);
    let originalCard: CardConfig | undefined;
    if (activeGroup) {
      const match = context.nestGroupConfigs.find(
        (cfg) => cfg.nestGroupValue === activeGroup.value,
      );
      if (match) {
        originalCard = match.cardConfigs.find(
          (card) => card.header.nestNumber === config.header.nestNumber,
        );
      }
    }

    const cardToUse = originalCard || config;

    context.selectedCardConfig = cardToUse;
    context.tempSelectedCardConfig = {
      ...cardToUse,
      rows: cardToUse.rows.map((r) => ({ ...r })),
    };

    context.originalTotalNests = context.total_nests;

    context.originalNestGroupConfigsSnapshot = context.nestGroupConfigs.map(
      (group) => ({
        ...group,
        cardConfigs: group.cardConfigs.map((card) => ({
          ...card,
          header: { ...card.header },
          stats: card.stats.map((s) => ({ ...s })),
          columns: [...card.columns],
          rows: card.rows.map((r) => ({ ...r })),
        })),
      }),
    );

    for (const group of context.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        const cardRows = card.rows.map((r) => ({ ...r }));
        context.allTempChanges.set(card.header.nestNumber, [...cardRows]);
        context.originalStateBeforePopup.set(card.header.nestNumber, [
          ...cardRows,
        ]);
      }
    }
    context.popupActiveNestGroup = activeGroup || null;
    if (activeGroup) {
      const match = context.nestGroupConfigs.find(
        (cfg) => cfg.nestGroupValue === activeGroup.value,
      );
      if (match) {
        context.tempCardConfigs = match.cardConfigs
          .filter(
            (card) => card.header.nestNumber !== cardToUse.header.nestNumber,
          )
          .map((card) => ({ ...card, rows: card.rows.map((r) => ({ ...r })) }));
      }
    }
    context.showMoveSkuPopup = true;
  }
  onDrop(
    context: NestingCardsPopupWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
    event: CdkDragDrop<RowData[]>,
    _target: "selected" | number,
  ): void {
    if (!event.previousContainer || !event.container) return;
    const sourceData = event.previousContainer.data;
    const targetData = event.container.data;
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      context.recalculateCardStats(sourceData);
      context.recalculateCardStats(targetData);
    }
    dependencies.saveTempChanges();
  }
  addNewNest(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    let newNestNumber: number;
    if (context.newNestGroupMapping.size === 0) {
      newNestNumber = context.total_nests;
    } else {
      const newNestNumbers = Array.from(context.newNestGroupMapping.keys());
      const maxNewNest = Math.max(...newNestNumbers);
      newNestNumber = maxNewNest + 1;
    }
    context.total_nests = Math.max(context.total_nests, newNestNumber + 1);
    let targetGroup: NestGroups | null = null;
    if (newNestNumber >= 0 && newNestNumber <= 5) {
      targetGroup =
        context.nestGroupsList.find((g) => g.value === "zeroToFive") || null;
    } else if (newNestNumber >= 6 && newNestNumber <= 10) {
      targetGroup =
        context.nestGroupsList.find((g) => g.value === "sixToTen") || null;
    } else if (newNestNumber >= 11) {
      targetGroup =
        context.nestGroupsList.find((g) => g.value === "moreThanTen") || null;
    }

    if (!targetGroup) {
      targetGroup = context.nestGroupsList[context.nestGroupsList.length - 1];
    }
    if (!targetGroup) return;
    if (context.popupActiveNestGroup?.value !== targetGroup.value) {
      context.toggleNestGroupInPopup(targetGroup);
    }

    const newCard: CardConfig = {
      header: { nestNumber: newNestNumber, pplRange: "-", beerType: "-" },
      stats: [
        { label: "# Brand", value: "-" },
        { label: "# Segment", value: "-" },
        { label: "# Pack Type", value: "-" },
        { label: "# Pack Size", value: "-" },
      ],
      columns: ["SKU Name", "PPL", "Segment", "Brand"],
      rows: [],
    };

    context.tempCardConfigs.push({ ...newCard, rows: [] });

    context.allTempChanges.set(newNestNumber, []);
    if (targetGroup) {
      context.newNestGroupMapping.set(newNestNumber, targetGroup.value);
    }
  }
  refreshPopupData(
    context: NestingCardsPopupWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    if (!context.selectedCardConfig || !context.popupActiveNestGroup) return;
    const selectedNestNumber = context.selectedCardConfig.header.nestNumber;
    let updatedSelectedCard: CardConfig | undefined;
    for (const group of context.nestGroupConfigs) {
      updatedSelectedCard = group.cardConfigs.find(
        (card) => card.header.nestNumber === selectedNestNumber,
      );
      if (updatedSelectedCard) break;
    }
    if (!updatedSelectedCard) {
      context.closeDialog();
      return;
    }
    context.selectedCardConfig = updatedSelectedCard;
    const savedSelectedRows = context.allTempChanges.get(selectedNestNumber);
    if (savedSelectedRows) {
      context.tempSelectedCardConfig = {
        ...updatedSelectedCard,
        rows: [...savedSelectedRows],
      };
    } else {
      context.tempSelectedCardConfig = {
        ...updatedSelectedCard,
        rows: updatedSelectedCard.rows.map((r) => ({ ...r })),
      };
      context.originalStateBeforePopup.set(
        selectedNestNumber,
        updatedSelectedCard.rows.map((r) => ({ ...r })),
      );
    }
    const match = context.nestGroupConfigs.find(
      (cfg) => cfg.nestGroupValue === context.popupActiveNestGroup?.value,
    );
    if (match) {
      context.tempCardConfigs = match.cardConfigs
        .filter((card) => card.header.nestNumber !== selectedNestNumber)
        .map((card) => {
          const savedRows = context.allTempChanges.get(card.header.nestNumber);
          const cardRows = savedRows
            ? [...savedRows]
            : card.rows.map((r) => ({ ...r }));
          if (!savedRows) {
            context.originalStateBeforePopup.set(
              card.header.nestNumber,
              card.rows.map((r) => ({ ...r })),
            );
          }

          return {
            ...card,
            rows: cardRows,
          };
        });
    }
  }
}
